import React, { useCallback, useState } from 'react';
import { ConfigProvider, Layout, Spin, App as AntApp } from 'antd';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ServersPage from './pages/ServersPage';
import ServerDetailPage from './pages/ServerDetailPage';
import HealthDetailPage from './pages/HealthDetailPage';
import ServerSnapshotPage from './pages/ServerSnapshotPage';
import ReportsPage from './pages/ReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import TicketsPage from './pages/TicketsPage';
import APIKeysPage from './pages/APIKeysPage';
import UsersPage from './pages/UsersPage';
import WebhooksPage from './pages/WebhooksPage';
import CheckLogsPage from './pages/CheckLogsPage';
import SettingsPage from './pages/SettingsPage';
import DiagnosticsPage from './pages/DiagnosticsPage';
import WorkerAnalyticsPage from './pages/WorkerAnalyticsPage';
import IntegrationDocsPage from './pages/IntegrationDocsPage';
import AppHeader from './components/AppHeader';
import AppSider from './components/AppSider';
import appTheme, { CONTENT_PADDING, SIDER_AUTO_COLLAPSE_WIDTH } from './theme';

const { Content } = Layout;

/** Remembers the rail state across sessions; narrow screens start collapsed. */
const SIDER_STORAGE_KEY = 'pnd:sider-collapsed';

const readStoredCollapsed = (): boolean => {
  const saved = localStorage.getItem(SIDER_STORAGE_KEY);
  if (saved === '1') return true;
  if (saved === '0') return false;
  return window.innerWidth < SIDER_AUTO_COLLAPSE_WIDTH;
};

const ProtectedRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><Spin size="large" /></div>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children || <Outlet />}</>;
};

const AppLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState<boolean>(readStoredCollapsed);
  const isAdmin = user?.role === 'admin';

  const toggleSider = useCallback(() => {
    setCollapsed((prev) => {
      localStorage.setItem(SIDER_STORAGE_KEY, prev ? '0' : '1');
      return !prev;
    });
  }, []);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppSider collapsed={collapsed} isAdmin={!!isAdmin} />
      <Layout>
        <AppHeader
          collapsed={collapsed}
          onToggleSider={toggleSider}
          email={user?.email}
          role={user?.role}
          isAdmin={!!isAdmin}
          onLogout={logout}
        />
        <Content style={{ padding: CONTENT_PADDING }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ConfigProvider componentSize="small" theme={appTheme}>
      <AntApp>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<AppLayout />}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/servers" element={<ServersPage />} />
                  <Route path="/servers/:key" element={<ServerDetailPage />} />
                  <Route path="/servers/:key/health" element={<HealthDetailPage />} />
                  <Route path="/servers/:key/snapshot" element={<ServerSnapshotPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/reports/:id" element={<ReportDetailPage />} />
                  <Route path="/tickets" element={<TicketsPage />} />
                  <Route path="/api-keys" element={<APIKeysPage />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/webhooks" element={<WebhooksPage />} />
                  <Route path="/check-logs" element={<CheckLogsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/diagnostics" element={<DiagnosticsPage />} />
                  <Route path="/worker-analytics" element={<WorkerAnalyticsPage />} />
                  <Route path="/integration-docs" element={<IntegrationDocsPage />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
